import { useState } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  ClipboardEdit,
  Pause,
  Play,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { RejectBusinessDialog } from "@/components/businesses/RejectBusinessDialog";
import { DeleteConfirmDialog } from "@/components/shared/DeleteConfirmDialog";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import {
  useBusiness,
  useDeleteBusiness,
  useToggleBusiness,
  useVerifyBusiness,
} from "@/hooks/useBusinesses";
import { useBusinessPendingEditRequest } from "@/hooks/useBusinessEditRequests";
import { OverviewTab } from "./tabs/OverviewTab";
import { LogoAvatar } from "@/components/shared/LogoAvatar";
import { ComplianceTab } from "./tabs/ComplianceTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { HistoryTab } from "./tabs/HistoryTab";
import { FacilityTab } from "./tabs/FacilityTab";
import { FoodMenuTab } from "./tabs/FoodMenuTab";
import { RoomsTab } from "./tabs/RoomsTab";
import { ReviewsTab } from "./tabs/ReviewsTab";
import { SubscriptionTab } from "./tabs/SubscriptionTab";
import { BranchesTab } from "./tabs/BranchesTab";
import { CareMedicalTab } from "./tabs/CareMedicalTab";
import { AnalyticsTab } from "./tabs/AnalyticsTab";

export const BusinessShow = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") ?? "overview";
  const { data: b, isLoading, error, refetch } = useBusiness(id);
  const { data: pendingEditRequest } = useBusinessPendingEditRequest(id);
  const verify = useVerifyBusiness();
  const toggle = useToggleBusiness();
  const del = useDeleteBusiness();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const docs = b?.documents ?? [];
  const allViewed = docs.length > 0 && docs.every((d) => !!d.viewedAt);
  const canApprove = b?.status === "PENDING" && allViewed;

  const approve = () => {
    if (!b) return;
    verify.mutate(
      { id: b.id, status: "APPROVED" },
      {
        onSuccess: () => toast.success("Business approved"),
        onError: (e: any) => {
          if (e?.data?.code === "DOCUMENTS_NOT_REVIEWED") {
            toast.error("Review all documents first");
          } else toast.error(e.message);
        },
      },
    );
  };

  const suspend = () => {
    if (!b) return;
    verify.mutate(
      { id: b.id, status: "SUSPENDED" },
      {
        onSuccess: () => toast.success("Business suspended"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const reactivate = () => {
    if (!b) return;
    verify.mutate(
      { id: b.id, status: "APPROVED" },
      {
        onSuccess: () => toast.success("Business reactivated"),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleReject = (reasons: string[], note?: string) => {
    if (!b) return;
    verify.mutate(
      { id: b.id, status: "REJECTED", reasons, note },
      {
        onSuccess: () => {
          toast.success("Business rejected");
          setRejectOpen(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const handleDelete = () => {
    if (!b) return;
    del.mutate(b.id, {
      onSuccess: () => {
        setDelOpen(false);
        toast.success("Business deleted");
        navigate("/businesses");
      },
      onError: (e) => toast.error(e.message),
    });
  };

  return (
    <AppLayout title="Business detail">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      ) : error ? (
        <ErrorCard
          message={(error as Error).message}
          onRetry={() => refetch()}
        />
      ) : !b ? null : (
        <>
          <div className="bg-card rounded-card border border-border p-5 mb-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <LogoAvatar src={b.logoUrl} name={b.businessName} size="lg" />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold">{b.businessName}</h2>
                  <StatusBadge status={b.status} />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {b.nameEn} · {b.city}
                  {b.owner ? (
                    <>
                      {" "}
                      · owner{" "}
                      <Link to={`/owners/${b.owner.id}`} className="underline">
                        {b.owner.name}
                      </Link>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                Visible
                <Switch
                  checked={b.isVisible}
                  disabled={b.status !== "APPROVED" || toggle.isPending}
                  onCheckedChange={(v) =>
                    toggle.mutate(
                      { id: b.id, isVisible: v },
                      { onError: (e) => toast.error(e.message) },
                    )
                  }
                />
              </label>
              {b.status === "PENDING" ? (
                <>
                  <PermissionGate module="businesses" action="approve">
                    <Button
                      onClick={approve}
                      disabled={!canApprove || verify.isPending}
                      title={
                        !canApprove ? "Review all documents first" : undefined
                      }
                    >
                      <CheckCircle className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                  </PermissionGate>
                  <PermissionGate module="businesses" action="reject">
                    <Button
                      variant="destructive"
                      onClick={() => setRejectOpen(true)}
                    >
                      <XCircle className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                  </PermissionGate>
                </>
              ) : null}
              {b.status === "APPROVED" ? (
                <PermissionGate module="businesses" action="suspend">
                  <Button
                    variant="outline"
                    onClick={suspend}
                    disabled={verify.isPending}
                  >
                    <Pause className="w-4 h-4 mr-1.5" /> Suspend
                  </Button>
                </PermissionGate>
              ) : null}
              {b.status === "SUSPENDED" || b.status === "REJECTED" ? (
                <PermissionGate module="businesses" action="approve">
                  <Button
                    variant="outline"
                    onClick={reactivate}
                    disabled={verify.isPending}
                  >
                    <Play className="w-4 h-4 mr-1.5" /> Reactivate
                  </Button>
                </PermissionGate>
              ) : null}
              <PermissionGate module="businesses" action="delete">
                <Button
                  variant="destructive"
                  onClick={() => setDelOpen(true)}
                  disabled={del.isPending}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              </PermissionGate>
            </div>
          </div>

          {pendingEditRequest ? (
            <div className="mb-4 rounded-card border border-warning/40 bg-warning/10 px-4 py-3 flex items-start gap-3">
              <ClipboardEdit className="w-5 h-5 mt-0.5 text-warning" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-warning">
                  Edit request pending review
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  The provider submitted{" "}
                  {Object.keys(pendingEditRequest.changes ?? {}).length} field
                  change(s) on{" "}
                  {new Date(pendingEditRequest.createdAt).toLocaleString()}.
                  Review it before applying any direct edits.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/edit-requests")}
              >
                Review
              </Button>
            </div>
          ) : null}

          <Tabs defaultValue={defaultTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="facility">Facility & photos</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
              <TabsTrigger value="documents">
                Documents ({docs.length}
                {docs.length > 0
                  ? `, ${docs.filter((d) => d.viewedAt).length} reviewed`
                  : ""}
                )
              </TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="food-menu">Food Menu</TabsTrigger>
              <TabsTrigger value="rooms">Rooms</TabsTrigger>
              <TabsTrigger value="branches">Branches</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="care-medical">Care &amp; Medical</TabsTrigger>
              <TabsTrigger value="analytics">Views &amp; Clicks</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              <OverviewTab b={b} />
            </TabsContent>
            <TabsContent value="facility" className="pt-4">
              <FacilityTab b={b} />
            </TabsContent>
            <TabsContent value="compliance" className="pt-4">
              <ComplianceTab b={b} />
            </TabsContent>
            <TabsContent value="documents" className="pt-4">
              <DocumentsTab b={b} />
            </TabsContent>
            <TabsContent value="history" className="pt-4">
              <HistoryTab businessId={b.id} />
            </TabsContent>
            <TabsContent value="food-menu" className="pt-4">
              <FoodMenuTab businessId={b.id} />
            </TabsContent>
            <TabsContent value="rooms" className="pt-4">
              <RoomsTab businessId={b.id} businessPhotos={b.photos ?? []} />
            </TabsContent>
            <TabsContent value="branches" className="pt-4">
              <BranchesTab businessId={b.id} />
            </TabsContent>
            <TabsContent value="reviews" className="pt-4">
              <ReviewsTab businessId={b.id} />
            </TabsContent>
            <TabsContent value="subscription" className="pt-4">
              <SubscriptionTab ownerId={b.owner?.id} />
            </TabsContent>
            <TabsContent value="care-medical" className="pt-4">
              <CareMedicalTab businessId={b.id} />
            </TabsContent>
            <TabsContent value="analytics" className="pt-4">
              <AnalyticsTab businessId={b.id} />
            </TabsContent>
          </Tabs>
        </>
      )}

      <RejectBusinessDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        businessName={b?.businessName}
        onSubmit={handleReject}
        submitting={verify.isPending}
      />
      <DeleteConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title={`Delete "${b?.businessName}"?`}
        description="This will permanently delete the business and all associated data. This action cannot be undone."
        onConfirm={handleDelete}
        loading={del.isPending}
      />
    </AppLayout>
  );
};

export default BusinessShow;
